import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import router from './router'
import i18n from './config/i18n'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import './assets/styles/main.css'
import '@fontsource/noto-sans-arabic/arabic-400.css'
import '@fontsource/noto-sans-arabic/arabic-700.css'
import App from './App.vue'

// Import stores for initialization
import { useAuthStore } from './stores/auth'
import { useLabelOverridesStore } from './stores/labelOverrides'
import { usePaymentMethodsStore } from './stores/paymentMethods'
import { useLocaleStore } from './stores/locale'
import { useStatusRegistryStore } from './stores/statusRegistry'
import { useThemeStore } from './stores/theme'
import { loggerService, toLoggableError } from './services/logger.service'
import {
  clearStaleChunkReloadFlag,
  registerStaleChunkRecoveryHandlers,
  reloadForStaleChunk,
} from './utils/staleChunkRecovery'
import { isServiceWorkerUpdateError } from './utils/swUpdateError'
import { sentryReplayOptions, sentryReplaysOnErrorSampleRate, beforeSendSentryEvent } from './utils/sentryConfig'

registerStaleChunkRecoveryHandlers()

const app = createApp(App)
const pinia = createPinia()

// Install plugins
app.use(pinia)
app.use(router)
app.use(i18n)

// Register global directives
import { permission } from './directives/permission'
app.directive('permission', permission)

// Reveal the full value of any clipped text on hover, focus or touch.
import { installTruncationAffordance } from './utils/truncationAffordance'
installTruncationAffordance()

// Configure toast notifications
app.use(Toast, {
  position: 'top-right',
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false // Will be set dynamically based on locale
})

// Global error handler
app.config.errorHandler = (err, _instance, info) => {
  loggerService.logError(
    toLoggableError(err),
    {
      action: 'vue_error',
      route: router.currentRoute.value.path
    }
  )
  
  // In development, log to console
  if (import.meta.env.DEV) {
    console.error('Global error:', err, info)
  }
}

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (reloadForStaleChunk(event.reason, 'unhandled-rejection')) {
    event.preventDefault()
    return
  }

  loggerService.logError(
    toLoggableError(event.reason),
    {
      // The browser's own automatic ServiceWorker update check rejects with no call site
      // to catch it at (see swUpdateError.ts) — route it through the same Sentry-excluded
      // action PwaUpdateBanner.vue's own update() catches use, instead of the generic one.
      action: isServiceWorkerUpdateError(event.reason)
        ? 'pwa_update_check_error'
        : 'unhandled_promise_rejection'
    }
  )

  // Prevent default browser error handling in production
  if (import.meta.env.PROD) {
    event.preventDefault()
  }
})

// Handle general errors
window.addEventListener('error', (event) => {
  loggerService.logError(
    event.error instanceof Error
      ? event.error
      : new Error(event.message || 'Unknown error'),
    {
      action: 'window_error',
      route: window.location.pathname
    }
  )
})

// Initialize stores after Pinia is installed
const authStore = useAuthStore()
const localeStore = useLocaleStore()
const themeStore = useThemeStore()

localeStore.initializeLocale()
themeStore.initializeTheme()

async function bootstrapApp() {
  // Initialize Sentry if DSN is configured
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_APP_ENV || 'development',
      release: import.meta.env.VITE_APP_VERSION,
      sendDefaultPii: false,
      attachErrorHandler: false,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(sentryReplayOptions),
      ],
      tracesSampleRate: import.meta.env.VITE_APP_ENV === 'production' ? 0.1 : 0.0,
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: sentryReplaysOnErrorSampleRate,
      beforeSend: beforeSendSentryEvent,
    })
  }

  await authStore.bootstrap()
  if (authStore.isAuthenticated) {
    void useLabelOverridesStore().load()
    void usePaymentMethodsStore().load()
    void useStatusRegistryStore().ensureLoaded()
  }
  // The Fabric-based Arabic font patch is only needed by the canvas editor. A dynamic
  // import keeps fabric out of the main chunk; the module still loads right after mount,
  // so the patch is installed before any editor is opened.
  void import('./canvas/arabicFontSupport').then((mod) => mod.preloadArabicFont())
  app.mount('#app')
  clearStaleChunkReloadFlag()
}

void bootstrapApp()

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  loggerService.destroy()
})
